import { Account } from '@radixdlt/radix-dapp-toolkit';
import BigNumber from 'bignumber.js';
import { useEffect, useState } from 'react';
import { Link, useFetcher, useLoaderData, useNavigate } from 'react-router';
import { LoaderFunctionArgs } from 'react-router';
import AccountSelector from '~/components/account-selector';
import PopoverHover from '~/components/popover';
import SimpleTokenSelector from '~/components/token-selector-simple';
import TokenInput from '~/components/token-input';
import TokenRow from '~/components/token-row';
import TransactionButton from '~/components/transaction-button';
import { fetchPrices } from '~/lib/astrolescent';
import { getCreatePoolDexConfigs, getDexConfigById } from '~/lib/dexes';
import { setDbFromContext } from '~/lib/db';
import { generateCreatePairManifest } from '~/lib/manifests';
import { getPairByBaseAndQuoteToken } from '~/lib/models/pair';
import { trackEvent } from '~/lib/analytics';
import { Token } from '~/lib/types';
import { XRD_RESOURCE_ADDRESS, bigZero, classNames, DEFAULT_PAIR_FEE } from '~/lib/util';
import { fetchTokensDetails } from '~/rdt/gateway';
import { useRadix } from '~/rdt/radixProvider';
import { loader as walletLoader } from '~/routes/wallet.$account';

BigNumber.config({
  DECIMAL_PLACES: 18,
  EXPONENTIAL_AT: 18,
});

export async function loader({ request, context }: LoaderFunctionArgs) {
  setDbFromContext(context);

  const url = new URL(request.url);
  const baseAddress = url.searchParams.get('baseToken');
  const selectedDex = getDexConfigById(url.searchParams.get('dex'));
  const createPoolDexes = getCreatePoolDexConfigs();
  const quoteTokens = await fetchTokensDetails(createPoolDexes.map((dex) => dex.quoteTokenAddress), true);
  const quoteTokensByAddress = Object.fromEntries(quoteTokens.map((token) => [token.address, token]));
  const quoteTokenOptions = createPoolDexes
    .map((dex) => {
      const quoteToken = quoteTokensByAddress[dex.quoteTokenAddress];

      if (!quoteToken) {
        return null;
      }

      return {
        dexId: dex.id,
        dexAddress: dex.dexAddress,
        minQuoteAmount: dex.minQuoteAmount,
        quoteToken,
      };
    })
    .filter((option): option is { dexId: string; dexAddress: string; minQuoteAmount: number; quoteToken: Token } => option !== null);

  const selectedQuoteTokenOption =
    quoteTokenOptions.find((option) => option.dexId === selectedDex.id) || quoteTokenOptions[0];

  if (!selectedQuoteTokenOption) {
    throw new Response('No quote token configuration found.', { status: 500 });
  }

  const quoteToken = selectedQuoteTokenOption.quoteToken;

  if (!baseAddress) {
    return {
      error: null,
      pairExist: false,
      existingPairAddress: null,
      baseToken: quoteToken,
      quoteToken,
      quoteTokenOptions,
      selectedDexId: selectedQuoteTokenOption.dexId,
      selectedDexAddress: selectedQuoteTokenOption.dexAddress,
      minQuoteAmount: selectedQuoteTokenOption.minQuoteAmount,
      defaultp0: '',
      quoteTokenPriceXRD: 0,
      quoteTokenPriceUSD: 0,
    };
  }

  const [baseToken] = await fetchTokensDetails([baseAddress], true);

  if (!baseToken) {
    return {
      error: 'Base token does not exist.',
      pairExist: false,
      existingPairAddress: null,
      baseToken: quoteToken,
      quoteToken,
      quoteTokenOptions,
      selectedDexId: selectedQuoteTokenOption.dexId,
      selectedDexAddress: selectedQuoteTokenOption.dexAddress,
      minQuoteAmount: selectedQuoteTokenOption.minQuoteAmount,
      defaultp0: '',
      quoteTokenPriceXRD: 0,
      quoteTokenPriceUSD: 0,
    };
  }

  const [row] = await getPairByBaseAndQuoteToken(baseAddress, quoteToken.address);

  if (row) {
    return {
      error: null,
      pairExist: true,
      existingPairAddress: row.Pair.address,
      baseToken,
      quoteToken,
      quoteTokenOptions,
      selectedDexId: selectedQuoteTokenOption.dexId,
      selectedDexAddress: selectedQuoteTokenOption.dexAddress,
      minQuoteAmount: selectedQuoteTokenOption.minQuoteAmount,
      defaultp0: '',
      quoteTokenPriceXRD: 0,
      quoteTokenPriceUSD: 0,
    };
  }

  const prices = await fetchPrices();
  const quoteTokenPriceXRD = prices[quoteToken.address]?.tokenPriceXRD;
  const quoteTokenPriceUSD = prices[quoteToken.address]?.tokenPriceUSD;

  let p0 = bigZero;
  if (prices[baseAddress]?.tokenPriceXRD && quoteTokenPriceXRD) {
    p0 = new BigNumber(prices[baseAddress].tokenPriceXRD).div(quoteTokenPriceXRD);
  }

  return {
    error: null,
    pairExist: false,
    existingPairAddress: null,
    baseToken,
    quoteToken,
    quoteTokenOptions,
    selectedDexId: selectedQuoteTokenOption.dexId,
    selectedDexAddress: selectedQuoteTokenOption.dexAddress,
    minQuoteAmount: selectedQuoteTokenOption.minQuoteAmount,
    defaultp0: p0.toFixed(baseToken.divisibility || 18),
    quoteTokenPriceXRD,
    quoteTokenPriceUSD,
  };
}

export default function LiquidityCreate() {
  const {
    baseToken,
    quoteToken,
    quoteTokenOptions,
    selectedDexId,
    selectedDexAddress,
    minQuoteAmount,
    defaultp0,
    pairExist,
    existingPairAddress,
    error,
    quoteTokenPriceXRD,
    quoteTokenPriceUSD,
  } = useLoaderData<typeof loader>();

  const [tokens, setTokens] = useState<Token[]>([]);
  const [p0, setP0] = useState(defaultp0);
  const [fee, setFee] = useState(DEFAULT_PAIR_FEE);
  const [baseTokensRequired, setBaseTokensRequired] = useState('');
  const quoteTokens = quoteTokenOptions.map((option) => option.quoteToken);

  const { sendTransaction, transaction } = useRadix();
  const [account, setAccount] = useState<string | null>(null);
  const walletFetcher = useFetcher<typeof walletLoader>();
  const navigate = useNavigate();

  function onAccount(account: Account) {
    setAccount(account.address);
    walletFetcher.load(`/wallet/${account.address}`);
  }

  useEffect(() => {
    if (!walletFetcher.data) {
      return;
    }

    walletFetcher.data.sort((a: Token, b: Token) => {
      if (a.address === XRD_RESOURCE_ADDRESS) {
        return -1;
      } else if (b.address === XRD_RESOURCE_ADDRESS) {
        return 1;
      } else if (a.name < b.name) {
        return -1;
      } else if (a.name > b.name) {
        return 1;
      }

      return 0;
    });

    setTokens(walletFetcher.data);
  }, [walletFetcher.data]);

  useEffect(() => {
    onP0Change(defaultp0);
  }, [baseToken, quoteToken, defaultp0]);

  function onBaseToken(token: Token) {
    const params = new URLSearchParams();
    params.set('baseToken', token.address);
    params.set('dex', selectedDexId);
    navigate(`?${params.toString()}`);
  }

  function onQuoteTokenChange(token: Token) {
    const params = new URLSearchParams();
    const option = quoteTokenOptions.find((quoteTokenOption) => quoteTokenOption.quoteToken.address === token.address);

    if (!option) {
      return;
    }

    if (baseToken?.address && baseToken.address !== quoteToken.address) {
      params.set('baseToken', baseToken.address);
    }

    params.set('dex', option.dexId);
    navigate(`?${params.toString()}`);
  }

  function onP0Change(p0String: string) {
    const p0 = parseFloat(p0String);

    setP0(p0String);

    if (!isNaN(p0) && p0 > 0) {
      const needed = new BigNumber(minQuoteAmount).div(p0).toFixed(baseToken.divisibility || 18);
      setBaseTokensRequired(needed);
    } else {
      setBaseTokensRequired('');
    }
  }

  function onBasePriceChange(price: string) {
    const p = parseFloat(price);

    if (!isNaN(p) && p > 0 && quoteTokenPriceXRD) {
      const priceInQuoteToken = new BigNumber(p).div(quoteTokenPriceXRD).toFixed(baseToken.divisibility || 18);
      onP0Change(priceInQuoteToken);
    }
  }

  function formatDerivedPrice(value: BigNumber) {
    if (!value.isFinite() || value.lte(0)) {
      return '0';
    }

    if (value.lt(0.0001)) {
      return value.toFixed(8).replace(/\.?0+$/, '');
    }

    if (value.lt(1)) {
      return value.toFixed(6).replace(/\.?0+$/, '');
    }

    return value.toFormat(4).replace(/\.?0+$/, '');
  }

  const quotePrice = new BigNumber(p0 || 0);
  const derivedPriceXRD = quotePrice.multipliedBy(quoteTokenPriceXRD || 0);
  const derivedPriceUSD = quotePrice.multipliedBy(quoteTokenPriceUSD || 0);

  async function onCreatePair() {
    if (!account) {
      return;
    }

    const transactionManifest = generateCreatePairManifest({
      account,
      baseTokenAddress: baseToken.address,
      baseTokenAmount: baseTokensRequired,
      quoteTokenAddress: quoteToken.address,
      quoteTokenAmount: `${minQuoteAmount}`,
      dexAddress: selectedDexAddress,
      p0,
      fee,
    });

    sendTransaction(transactionManifest, `Create ${baseToken.symbol}/${quoteToken.symbol} pool on Reddicks`);

    trackEvent("send_tx", {
      type: "createPool",
    });
  }

  return (
    <div className="mx-4 mb-8 xl:mx-auto xl:w-3/4">
      <div className="flex items-center justify-between">
        <h1 className="py-4 text-left text-3xl font-bold lg:p-8 lg:text-4xl">Create new pool</h1>
      </div>

      <section className="lg:items-top flex flex-col-reverse lg:flex-row lg:space-x-4">
        <div className="grow space-y-4">
          <AccountSelector mode="light" onAccount={onAccount}></AccountSelector>

          <section className="flex flex-col rounded-xl bg-white p-4 dark:border dark:border-white/[0.08] dark:bg-white/[0.04] dark:_backdrop-blur-xl lg:rounded-2xl lg:p-8">
            <div className="flex items-center justify-between">
              <label htmlFor="tokenInput" className="text-sm text-primary-600 dark:text-primary-300">
                Select tokens
              </label>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <SimpleTokenSelector name="inputToken" tokens={tokens} selected={baseToken} onSelected={onBaseToken} />
              <SimpleTokenSelector name="quoteToken" tokens={quoteTokens} selected={quoteToken} onSelected={onQuoteTokenChange} />
            </div>
          </section>

          {error ? <p className="shadow-xs text-md mt-2 rounded-md bg-red-800 px-4 py-1 text-center text-base font-medium text-white">{error}</p> : null}

          {pairExist ? (
            <section className="rounded-xl bg-white p-4 text-center text-black dark:border dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-text-primary dark:_backdrop-blur-xl lg:rounded-2xl lg:p-8">
              The {baseToken.symbol}/{quoteToken.symbol} pool already exists, you can{" "}
              <Link to={`../add/${existingPairAddress}?direction=base`} className="text-primary-600 dark:text-primary-300">
                add liquidity
              </Link>{" "}
              instead.
            </section>
          ) : null}

          {transaction?.intentHash ? (
            <section className="rounded-xl bg-white p-4 text-center text-black dark:border dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-text-primary dark:_backdrop-blur-xl lg:rounded-2xl lg:p-8">
              The transaction for the {baseToken.symbol}/{quoteToken.symbol} pool has been submitted. You can now{" "}
              <Link to="/liquidity/add" className="text-primary-600 dark:text-primary-300">
                return to the pools list
              </Link>{" "}
              while the pool is picked up by the discovery cron.
            </section>
          ) : null}

          {!pairExist && !error && !transaction?.intentHash ? (
            <>
              <section className="mt-8 flex flex-col rounded-xl bg-white p-4 dark:border dark:border-white/[0.08] dark:bg-white/[0.04] dark:_backdrop-blur-xl lg:rounded-2xl lg:p-8">
                <label htmlFor="tokenInput" className="text-sm text-primary-600 dark:text-primary-300">
                  Trading fee
                </label>
                <span className="isolate inline-flex rounded-md text-black dark:text-text-primary">
                  <button
                    type="button"
                    onClick={() => setFee(0.004)}
                    className={classNames(fee == 0.004 ? "ring-primary-500 ring-2" : "ring-1 ring-gray-300 dark:ring-white/[0.1]", "relative grow rounded-l-md bg-white px-3 py-2 ring-inset hover:bg-gray-50 dark:bg-white/[0.04] dark:hover:bg-white/[0.08] focus:z-10")}
                  >
                    <span>
                      <p className="text-primary-500 text-2xl">0.4%</p>
                      <p>Best for project tokens</p>
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFee(0.01)}
                    className={classNames(fee !== 0.004 ? "ring-primary-500 ring-2" : "ring-1 ring-gray-300 dark:ring-white/[0.1]", "relative grow rounded-r-md bg-white px-3 py-2 ring-inset hover:bg-gray-50 dark:bg-white/[0.04] dark:hover:bg-white/[0.08] focus:z-10")}
                  >
                    <span>
                      <p className="text-primary-500 text-2xl">1%</p>
                      <p>Best for meme coins</p>
                    </span>
                  </button>
                </span>
              </section>

              <section className="flex flex-col rounded-xl bg-white p-4 dark:border dark:border-white/[0.08] dark:bg-white/[0.04] dark:_backdrop-blur-xl lg:rounded-2xl lg:p-8">
                <div className="flex items-center justify-between">
                  <label htmlFor="tokenInput" className="text-sm text-primary-600 dark:text-primary-300">
                    <PopoverHover title={`Price of ${baseToken?.symbol || "base token"} in ${quoteToken.symbol}`}>
                      <div className="w-40 space-y-2 lg:w-60">
                        <p>
                          You can calculate the price of {baseToken?.symbol} in {quoteToken.symbol} by dividing the XRD price of {baseToken?.symbol} by the XRD price of {quoteToken.symbol}.
                        </p>
                        <p>You can also fill in the price of {baseToken?.symbol} in XRD below and we calculate the right price for you:</p>
                        <p className="border-primary-200 flex items-center border px-1">
                          <input
                            type="text"
                            onChange={(evt) => onBasePriceChange((evt.target as HTMLInputElement).value)}
                            placeholder="0.00"
                            autoComplete="off"
                            spellCheck="false"
                            className="mr-4 flex-auto border-none bg-transparent p-0 text-black placeholder:text-primary-200 dark:text-text-primary dark:placeholder:text-primary-600/80"
                          />
                          XRD
                        </p>
                        <p>
                          Price in {quoteToken.symbol}: {p0}
                        </p>
                      </div>
                    </PopoverHover>
                  </label>
                </div>
                <div className="flex flex-wrap items-center justify-between">
                  <TokenInput value={p0} onChange={onP0Change} />
                  <span className="text-2xl font-medium text-black dark:text-text-primary">{quoteToken.symbol}</span>
                </div>
                <div className="mt-4 space-y-1 text-sm text-primary-700/70 dark:text-primary-200/70">
                  <p>
                    Price in XRD: <span className="font-medium text-black dark:text-text-primary/70">{formatDerivedPrice(derivedPriceXRD)} XRD</span>
                  </p>
                  <p>
                    Price in USD: <span className="font-medium text-black dark:text-text-primary/70">${formatDerivedPrice(derivedPriceUSD)}</span>
                  </p>
                </div>
              </section>

              <p className="flex justify-between px-8 py-4">
                <svg className="h-6 w-6" viewBox="8 8 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M29.088 21.972c.432-.443.432-1.212 0-1.654a1.128 1.128 0 0 0-1.617 0l-6.322 6.466V10.58c0-.645-.513-1.17-1.144-1.17-.63 0-1.143.525-1.143 1.17v16.203l-6.321-6.466a1.128 1.128 0 0 0-1.618 0 1.178 1.178 0 0 0-.335.827c0 .312.12.606.336.827l7.964 8.143a1.556 1.556 0 0 0 2.237 0l7.963-8.143z"
                    fill="currentColor"
                    fillRule="nonzero"
                  ></path>
                </svg>
              </p>

              <section className="flex flex-col rounded-xl bg-white p-4 dark:border dark:border-white/[0.08] dark:bg-white/[0.04] dark:_backdrop-blur-xl lg:rounded-2xl lg:p-8">
                <div className="flex items-center justify-between">
                  <label htmlFor="tokenInput" className="text-sm text-primary-600 dark:text-primary-300">
                    Base Tokens
                  </label>
                </div>
                <div className="flex flex-wrap items-center justify-between">
                  <TokenInput value={baseTokensRequired} onChange={() => {}} />

                  <TokenRow token={baseToken} />
                </div>
              </section>

              <section className="flex flex-col rounded-xl bg-white p-4 dark:border dark:border-white/[0.08] dark:bg-white/[0.04] dark:_backdrop-blur-xl lg:rounded-2xl lg:p-8">
                <div className="flex items-center justify-between">
                  <label htmlFor="tokenInput" className="text-sm text-primary-600 dark:text-primary-300">
                    Quote Tokens
                  </label>
                </div>
                <div className="flex flex-wrap items-center justify-between">
                  <TokenInput value={"" + minQuoteAmount} onChange={() => {}} />

                  <TokenRow token={quoteToken} />
                </div>
              </section>

              <TransactionButton title="Create New Pool" onClick={onCreatePair} />
            </>
          ) : null}
        </div>

        <div className="flex flex-col space-y-4 rounded-xl bg-white p-8 dark:border dark:border-white/[0.08] dark:bg-white/[0.04] dark:_backdrop-blur-xl lg:w-96 lg:rounded-2xl">
          <p className="text-primary-700 dark:text-primary-100">Creating a new pair</p>
          <p>
            When creating a new pool an amount of base tokens and {quoteToken.symbol} must be provided which will be <strong className="text-primary-700 dark:text-primary-100">donated</strong> to
            the protocol.
          </p>
          <p>This minimum amount of liquidity is used to prevent the pool from ever running empty, which could lead to price manipulation.</p>
          <p>
            After creating the pair, you can <Link to="/liquidity/add">add liquidity</Link>.
          </p>

          <p className="text-primary-700 dark:text-primary-100">Please note</p>
          <p>There can be only one pool per base token and quote-token combination.</p>
          <p>It is not possible to set the fee and concentration levels of new pools. This might be added in the future. Pools that are created are not updateable.</p>

          <p className="text-primary-700 dark:text-primary-100">Pool governance</p>
          <p>The Reddicks DAO can delist pools that might harm the normal operation of the DEX. Examples are:</p>
          <ul className="list-disc pl-5">
            <li>Pools created with a price of the base token in {quoteToken.symbol} which is far away from the market price, to prevent price manipulation.</li>
            <li>Pools with base tokens that are considered a scam.</li>
          </ul>
          <p>Pools that are delisted can still be used by interacting with them directly outside of the Reddicks UI. Delisting only removes them from the UI and the main DEX component.</p>
        </div>
      </section>
    </div>
  );
}
